// useBookingFlowNav — owns the refs + smooth-scroll behaviour that
// coordinates the widget's three progressive steps
// (Accommodation → Extras → Details).
//
// The `flowStep` value itself lives in the shared BookingContext so
// the widget, the sticky summary, and any other surface can move the
// user between steps in lock-step. This hook only wires the DOM side:
//
//   • Exposes `step2Ref`, `step3Ref` — attach to the step sections.
//   • `goToStep(n)` — sets the context step AND smooth-scrolls into
//     view. Skips scroll on step 1 (that's the widget's anchor point).
//   • Watches external `flowStep` changes (e.g. from StickySummary
//     "Continue" buttons) and mirrors the same smooth-scroll behaviour
//     so users never end up on an unseen step.
//
// Extracted from `HomeBookingWidget.jsx` — behaviour is 1:1 with the
// original inline logic.

import { useCallback, useEffect, useRef } from "react";
import { useBooking } from "@/contexts/BookingContext";

export default function useBookingFlowNav({ isStandalone }) {
  const { flowStep, setFlowStep } = useBooking();
  const step2Ref = useRef(null);
  const step3Ref = useRef(null);

  const goToStep = useCallback(
    (n) => {
      setFlowStep(n);
      window.setTimeout(() => {
        const targetRef = n === 2 ? step2Ref : n === 3 ? step3Ref : null;
        targetRef?.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 60);
    },
    [setFlowStep]
  );

  useEffect(() => {
    if (!isStandalone) return;
    if (flowStep === 2) {
      step2Ref.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    } else if (flowStep === 3) {
      step3Ref.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [flowStep, isStandalone]);

  return { flowStep, setFlowStep, goToStep, step2Ref, step3Ref };
}
